import React from 'react';
import PropTypes from 'prop-types';

import { useLocalStorage, LS_KEYS } from '../hooks/useLocalStorage';
import { useJwt } from '../hooks';

const MainContext = React.createContext();

const BackofficeContextProvider = ({ children }) => {
  const { lsValues, setLSItem, getLSItem, removeLSItem } = useLocalStorage(
    LS_KEYS.BACKOFFICE,
  );
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [success, setSuccess] = React.useState('');
  const [menu, setMenu] = React.useState([]);
  const { getJwtPayload } = useJwt();

  const loadingOn = React.useCallback(() => {
    setIsLoading(true);
  }, []);

  const loadingOff = React.useCallback(() => {
    setIsLoading(false);
  }, []);

  const removeSuccess = React.useCallback(() => {
    setSuccess('');
  }, [setSuccess]);

  const removeError = React.useCallback(() => {
    setError('');
  }, [setError]);

  const sortAccess = (left, right) => {
    if (left.title > right.title) {
      return 1;
    }
    if (left.title < right.title) {
      return -1;
    }
    return 0;
  };

  const setAccess = React.useCallback(access => {
    const menuObj = access
      .filter(item => !item.parent)
      .sort(sortAccess)
      .map(({ _id: parentId, title }) => ({
        title,
        items: access.filter(item => item.parent === parentId).sort(sortAccess),
      }));
    setMenu(menuObj);
  }, []);

  React.useEffect(() => {
    const auth = getLSItem('auth');
    if (!menu.length && auth) {
      const { token } = auth;
      const {
        access: { items: access },
      } = getJwtPayload(token);
      setAccess(access);
    }
  }, [getJwtPayload, getLSItem, menu, setAccess]);

  const getAuth = React.useCallback(() => getLSItem('auth'), [getLSItem]);

  return (
    <MainContext.Provider
      value={{
        menu,
        setLSItem,
        removeLSItem,
        getLSItem,
        lsValues,
        loadingOn,
        loadingOff,
        isLoading,
        setError,
        removeError,
        error,
        setSuccess,
        removeSuccess,
        success,
        getAuth,
        setAccess,
      }}
    >
      {children}
    </MainContext.Provider>
  );
};

const useBackofficeContext = () => {
  const context = React.useContext(MainContext);

  if (!context) {
    throw new Error(
      'useBackofficeContext must be use with BackofficeContextProvider',
    );
  }

  return context;
};

BackofficeContextProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
};

export { useBackofficeContext, BackofficeContextProvider };
